/**
 * Formatting utilities for displaying data in the UI
 *
 * Core formatters:
 * - formatDate: Converts 'YYYY-MM-DD' to 'MM/DD/YYYY'
 * - formatTime: Formats time strings to 12-hour format with AM/PM
 * - formatMoney: Currency formatting with Intl.NumberFormat (proper localization, thousand separators)
 * - formatPrice: Bond price formatting with flexible decimal precision (default 4 decimals)
 * - formatPercentage: Percentage with configurable decimal places
 * - formatPercentageAuto: Percentage with automatic decimal precision based on value
 * - formatNumber: General number formatting with optional pluralization
 * - formatDecimal: Raw decimal number formatting
 * - formatBoolean: Converts boolean to 'Yes'/'No' display
 *
 * Text transformation utilities:
 * - convertToTitleCase: Converts strings to Title Case
 * - convertSnakeCaseToSentenceCase: Converts snake_case to sentence case
 * - convertTitleCaseToSnakeCase: Converts Title Case to snake_case
 * - pluralize: Adds singular/plural suffix based on count
 *
 * Specialized formatters:
 * - formatAssetClass: Converts BondType enum to display string
 * - decimalToPercentage: Converts decimal to percentage value
 *
 * Helper functions:
 * - isNullish: Checks if value is null/undefined/empty/NaN
 * - isValidPositiveIntegerInput: Validates positive integer input
 * - defaultKeyTransform: Default key transformation for objects
 * - defaultValueTransform: Default value transformation for display
 */

import { BondType } from '@moment/trading-types/reference-data'
import { pluralize } from '@moment/utils/format'
import Decimal from 'decimal.js-light'

export { pluralize }

export const PLACEHOLDER = '—'

/**
 * Checks if a given value is "nullish".
 *
 * A value is considered "nullish" if it is:
 * - `null`
 * - `undefined`
 * - An empty string `''`
 * - A number that is `NaN`
 *
 * @param value - The value to check.
 * @returns `true` if the value is nullish, otherwise `false`.
 */
export function isNullish<T>(value: T): boolean {
  return (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '') ||
    // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
    (typeof value === 'number' && isNaN(value))
  )
}

/**
 * First value that carries real text, or null. Use this over `??` for label
 * chains: an instrument built from a Go struct sends `''`, which `??` accepts.
 */
export function firstNonBlank(
  ...values: (string | null | undefined)[]
): string | null {
  for (const value of values) {
    if (!isNullish(value)) return value as string
  }
  return null
}

/**
 * Formats a date string in the format 'YYYY-MM-DD' to 'MM/DD/YYYY',
 * or 'YYYY-MM' to 'MM/YYYY' if the day is not present.
 *
 * If the input date is nullish (null or an empty string),
 * it returns a placeholder string. If the date does not match the
 * expected format, it returns 'Invalid Date'.
 *
 * @param date - The date string to format, expected in 'YYYY-MM-DD' or 'YYYY-MM' format.
 * @returns The formatted date string in 'MM/DD/YYYY' format or 'MM/YYYY' format
 *          depending on the input format, or a placeholder if the input is nullish,
 *          or 'Invalid Date' if the format is incorrect.
 *          The returned date is not localized.
 */
export function formatDate(date: string | null): string {
  if (isNullish(date)) {
    return PLACEHOLDER
  }
  // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  const yearMonthDayMatch = date!.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!yearMonthDayMatch) {
    // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
    const yearMonthMatch = date!.match(/^(\d{4})-(\d{2})/)
    if (!yearMonthMatch) {
      return 'Invalid Date'
    }
    const [, year, month] = yearMonthMatch
    return `${
      // biome-ignore lint/correctness/useParseIntRadix: Bulk suppression as part of biome migration
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      parseInt(month!)
    }/${year}`
  }
  const [, year, month, day] = yearMonthDayMatch
  return `${
    // biome-ignore lint/correctness/useParseIntRadix: Bulk suppression as part of biome migration
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    parseInt(month!)
    // biome-ignore lint/correctness/useParseIntRadix: Bulk suppression as part of biome migration
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
  }/${parseInt(day!)}/${year}`
}

/**
 * Formats a time string into a localized date and time string.
 *
 * If the input time is nullish (null or an empty string),
 * it returns a placeholder string. Otherwise, it converts the time
 * string into a localized date and time format using the 'en-US' locale.
 *
 * @param time - The time string to format, expected in a format
 *               that can be parsed by the Date constructor.
 * @param options - Optional formatting options to customize the output format.
 *                 If not provided, uses default format with day, month, year,
 *                 hour, minute, and second.
 * @returns The formatted date and time string, or a placeholder
 *          if the input is nullish.
 */
export function formatTime(
  time: string | null,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (isNullish(time)) {
    return PLACEHOLDER
  }
  // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  return new Date(time!).toLocaleString(
    'en-US',
    options ?? {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    },
  )
}

interface FormatMoneyOptions {
  numDigits?: number
  useZeroPlaceholder?: boolean
  addTrailingZeros?: boolean
  currency?: string
}

/**
 * Formats a given price as a currency string in USD.
 *
 * If the input price is nullish (null or NaN),
 * it returns a placeholder string. If the price is zero and
 * the `useZeroPlaceholder` option is set to true, it also
 * returns a placeholder string.
 *
 * @param price - The price to format, which can be a number or null.
 * @param options - An optional object to specify formatting options:
 *   - `numDigits`: The number of decimal places to include in the formatted string.
 *   - `useZeroPlaceholder`: A boolean indicating whether to use a placeholder for zero values.
 *   - `addTrailingZeros`: A boolean indicating whether to add trailing zeros to the formatted string.
 * @returns The formatted currency string in USD, or a placeholder if the input is nullish or zero (when `useZeroPlaceholder` is true).
 */
export function formatMoney(
  price: number | null,
  options: FormatMoneyOptions = {
    numDigits: 2,
    useZeroPlaceholder: false,
    addTrailingZeros: false,
  },
): string {
  if (isNullish(price)) {
    return PLACEHOLDER
  }

  if (price === 0 && options.useZeroPlaceholder) {
    return PLACEHOLDER
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: options.currency ?? 'USD',
    minimumFractionDigits: options.addTrailingZeros
      ? options.numDigits
      : undefined,
    maximumFractionDigits: options.numDigits,
  })

  return formatter.format(Number(price))
}

interface FormatSignedMoneyOptions extends FormatMoneyOptions {
  /** Detach the sign from the amount on both directions: `+ $5` / `- $5`. */
  spacedSign?: boolean
}

/**
 * `formatMoney` with an explicit `+` on gains, so a value's direction reads on
 * its own. Negatives already carry the formatter's minus; zero stays unsigned.
 */
export function formatSignedMoney(
  value: number | null,
  options: FormatSignedMoneyOptions = {},
): string {
  const { spacedSign = false, ...moneyOptions } = options
  if (!spacedSign) {
    const formatted = formatMoney(value, moneyOptions)
    const isGain = value !== null && value > 0
    return isGain ? `+${formatted}` : formatted
  }
  const formatted = formatMoney(
    value === null ? null : Math.abs(value),
    moneyOptions,
  )
  if (value === null || value === 0 || Number.isNaN(value)) return formatted
  return `${value > 0 ? '+' : '-'} ${formatted}`
}

/**
 * Formats a given price as a string with a dollar sign.
 *
 * If the input price is nullish (null or NaN),
 * it returns a placeholder string. If the price is zero and
 * the `useZeroPlaceholder` option is set to true, it also
 * returns a placeholder string. If the `shouldTruncateIfWhole`
 * option is set to true and the price is a whole number, it
 * truncates the decimal part.
 *
 * @param price - The price to format, which can be a number or null.
 * @param options - An optional object to specify formatting options:
 *   - `shouldTruncateIfWhole`: A boolean indicating whether to truncate
 *     the decimal part if the price is a whole number.
 *   - `useZeroPlaceholder`: A boolean indicating whether to use a placeholder
 *     for zero values.
 * @returns The formatted price string with a dollar sign, or a placeholder
 *          if the input is nullish or zero (when `useZeroPlaceholder` is true).
 */
export function formatPrice(
  price: number | null | undefined,
  options: {
    shouldTruncateIfWhole?: boolean
    useZeroPlaceholder?: boolean
    addTrailingZeros?: boolean
  } = {
    shouldTruncateIfWhole: false,
    useZeroPlaceholder: false,
    addTrailingZeros: false,
  },
  digits = 4,
): string {
  if (isNullish(price)) {
    return PLACEHOLDER
  }
  // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  if (price! === 0 && options.useZeroPlaceholder) {
    return PLACEHOLDER
  }
  if (options.shouldTruncateIfWhole && Number.isInteger(price)) {
    return `$${
      // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
      formatDecimal(price!, 0)
    }`
  }

  // Determine how many decimal places to show
  const decimalPlaces = (price as number).toString().split('.')[1]?.length || 0
  const digitsToShow = options.addTrailingZeros
    ? digits
    : Math.min(decimalPlaces, digits)

  // If we need to add trailing zeros, use a formatter with minimum fraction digits
  if (options.addTrailingZeros) {
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: digitsToShow,
      maximumFractionDigits: digitsToShow,
    })
    return `$${
      // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
      formatter.format(Number(price!))
    }`
  }

  return `$${
    // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
    formatDecimal(price!, digitsToShow)
  }`
}

/**
 * Formats a given percentage as a string with a specified number of decimal places.
 *
 * If the input percentage is nullish (null or NaN),
 * it returns a placeholder string. If the percentage is zero and
 * the `useZeroPlaceholder` option is set to true, it also returns
 * a placeholder string. The number of decimal places can be specified
 * using the `numDigits` option. If `numDigits` is null, the percentage
 * will not be rounded.
 *
 * @param percentage - The percentage to format, which can be a number or null.
 * @param options - An optional object to specify formatting options:
 *   - `numDigits`: The number of decimal places to include in the formatted string.
 *   - `useZeroPlaceholder`: A boolean indicating whether to use a placeholder for zero values.
 *   - `removeTrailingZeros`: A boolean indicating whether to remove trailing zeros after the decimal point.
 * @returns The formatted percentage string with a '%' sign, or a placeholder
 *          if the input is nullish or zero (when `useZeroPlaceholder` is true).
 */
export function formatPercentage(
  percentage: number | null,
  options: {
    /* If null, we will not round the percentage */
    numDigits?: number | null
    useZeroPlaceholder?: boolean
    removeTrailingZeros?: boolean
  } = {
    numDigits: 4,
    useZeroPlaceholder: false,
    removeTrailingZeros: false,
  },
): string {
  if (isNullish(percentage)) {
    return PLACEHOLDER
  }
  // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  if (percentage! === 0 && options.useZeroPlaceholder) {
    return PLACEHOLDER
  }

  // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  let formattedValue = formatDecimal(percentage!, options.numDigits)

  // Remove trailing zeros if the option is enabled
  if (options.removeTrailingZeros && formattedValue.includes('.')) {
    formattedValue = formattedValue.replace(/\.?0+$/, '')
  }

  return `${formattedValue}%`
}

export function formatPercentageAuto(
  value: number,
  precision: number = 3,
): string {
  return formatPercentage(value, { numDigits: value === 0 ? 0 : precision })
}

export function decimalToPercentage(value: number): number {
  return new Decimal(value).mul(100).toNumber()
}

/**
 * Formats a given number according to specified options.
 *
 * If the input number is nullish (null or NaN),
 * it returns a placeholder string. If the number is zero and
 * the `useZeroPlaceholder` option is set to true, it also
 * returns a placeholder string. The number of decimal places
 * can be controlled using the `minimumFractionDigits` and
 * `maximumFractionDigits` options.
 *
 * @param value - The number to format, which can be a number or null.
 * @param options - An optional object to specify formatting options:
 *   - `useZeroPlaceholder`: A boolean indicating whether to use a placeholder
 *     for zero values.
 *   - `minimumFractionDigits`: The minimum number of decimal places to include
 *     in the formatted string.
 *   - `maximumFractionDigits`: The maximum number of decimal places to include
 *     in the formatted string.
 * @returns The formatted number string, or a placeholder if the input is nullish
 *          or zero (when `useZeroPlaceholder` is true).
 */
export function formatNumber(
  value: number | null,
  options: {
    useZeroPlaceholder?: boolean
    minimumFractionDigits?: number
    maximumFractionDigits?: number
    useGrouping?: boolean
  } = {
    useZeroPlaceholder: false,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  },
): string {
  if (isNullish(value)) {
    return PLACEHOLDER
  }

  // When useZeroPlaceholder is true, check if value is below display threshold
  if (options.useZeroPlaceholder) {
    // Use the maximum of minimumFractionDigits and maximumFractionDigits for threshold
    const decimalPlaces = Math.max(
      options.minimumFractionDigits ?? 0,
      options.maximumFractionDigits ?? 0,
    )
    const threshold = decimalPlaces > 0 ? 10 ** -decimalPlaces : 0

    // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
    if (Math.abs(value!) < threshold || value! === 0) {
      return PLACEHOLDER
    }
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: options.minimumFractionDigits,
    maximumFractionDigits: options.maximumFractionDigits,
    useGrouping: options.useGrouping,
    // Suppress the minus on -0 and on negatives that round to zero.
    signDisplay: 'negative',
    // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  }).format(value!)
}

/**
 * Formats a given decimal number to a string with a specified number of decimal places.
 *
 * If the input decimal is nullish (null or NaN), it returns a placeholder string.
 * The number of decimal places can be specified using the `numDigits` parameter.
 *
 * @param decimal - The decimal number to format, which can be a number or null.
 * @param numDigits - The number of decimal places to include in the formatted string. Defaults to 3.
 * @returns The formatted decimal string with the specified number of decimal places, or a placeholder if the input is nullish.
 */
export function formatDecimal(
  decimal: number | null,
  numDigits: number | null = 3,
): string {
  if (isNullish(decimal)) {
    return PLACEHOLDER
  }
  if (numDigits === null) {
    // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
    return decimal!.toString()
  }
  // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  return Number(decimal!).toFixed(numDigits!)
}

/**
 * Formats a boolean value as a string.
 *
 * If the input boolean is nullish (null or undefined),
 * it returns a placeholder string. Otherwise, it returns
 * 'Yes' for true and 'No' for false.
 *
 * @param bool - The boolean value to format, which can be a boolean or null.
 * @returns The formatted string 'Yes' or 'No', or a placeholder if the input is nullish.
 */
export function formatBoolean(bool: boolean | null): string {
  if (isNullish(bool)) {
    return PLACEHOLDER
  }
  // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  return bool! ? 'Yes' : 'No'
}

/**
 * Formats a boolean as "Enabled" / "Disabled" (e.g. account-level toggles).
 */
export function formatBooleanEnabledDisabled(bool: boolean): string {
  return bool ? 'Enabled' : 'Disabled'
}

/**
 * Formats a boolean as "Available" / "Not available" (e.g. prime brokerage, margin trading).
 */
export function formatBooleanAvailable(bool: boolean): string {
  return bool ? 'Available' : 'Not available'
}

/**
 * Converts a given string to Title Case.
 *
 * If the input string is nullish (null or an empty string),
 * it returns a placeholder string. Otherwise, it capitalizes
 * the first letter of each word and converts the rest of the
 * letters to lowercase.
 *
 * @param key - The string to convert to title case, which can be a string or null.
 * @returns The converted string in title case, or a placeholder if the input is nullish.
 */
export function convertToTitleCase(key: string | null): string {
  if (isNullish(key)) {
    return PLACEHOLDER
  }
  // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  return key!
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Converts a snake_case string to Sentence case.
 *
 * If the input string is nullish (null or an empty string),
 * it returns a placeholder string. Otherwise, it splits the
 * string by underscores, capitalizes the first letter of the
 * first word, and joins them with spaces.
 *
 * @param key - The snake_case string to convert, which can be a string or null.
 * @returns The converted string in Sentence case, or a placeholder if the input is nullish.
 */
export function convertSnakeCaseToSentenceCase(key: string | null): string {
  if (isNullish(key)) {
    return PLACEHOLDER
  }
  // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  return key!
    .split('_')
    .filter((word) => word !== '')
    .map((word, index) =>
      index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1)
        : word.toLowerCase(),
    )
    .join(' ')
}

/**
 * Converts a Title Case or any cased string to Sentence case.
 *
 * If the input string is nullish (null or an empty string),
 * it returns a placeholder string. Otherwise, it capitalizes
 * only the first letter of the first word and converts all
 * other letters to lowercase, preserving spaces.
 *
 * @param text - The string to convert to sentence case, which can be a string or null.
 * @returns The converted string in Sentence case, or a placeholder if the input is nullish.
 */
export function convertToSentenceCase(text: string | null): string {
  if (isNullish(text)) {
    return PLACEHOLDER
  }
  // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  return text!
    .split(' ')
    .map((word, index) =>
      index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase(),
    )
    .join(' ')
}

/**
 * Converts a Title Case string to snake_case.
 *
 * If the input string is nullish (null or an empty string),
 * it returns a placeholder string. Otherwise, it splits the
 * string by spaces, converts the first letter of each word to
 * lowercase, and joins them with underscores.
 *
 * @param title - The Title Case string to convert, which can be a string or null.
 * @returns The converted string in snake_case, or a placeholder if the input is nullish.
 */
export function convertTitleCaseToSnakeCase(title: string | null): string {
  if (isNullish(title)) {
    return PLACEHOLDER
  }
  // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  return title!
    .split(' ')
    .filter((word) => word !== '')
    .map((word) => word.charAt(0).toLowerCase() + word.slice(1))
    .join('_')
}

/**
 * Transforms a given key from snake_case to Title Case.
 *
 * If the input key is nullish (null or an empty string),
 * it returns a placeholder string. Otherwise, it converts
 * the snake_case key to Title Case by capitalizing the first
 * letter of each word and joining them with spaces.
 *
 * @param key - The snake_case string to transform, which can be a string or null.
 * @returns The transformed string in Sentence case, or a placeholder if the input is nullish.
 */
export function defaultKeyTransform(key: string): string {
  if (isNullish(key)) {
    return PLACEHOLDER
  }
  // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  return convertSnakeCaseToSentenceCase(key!)
}

/**
 * Transforms a given value into a string representation.
 *
 * If the input value is nullish (null, undefined, or an empty array),
 * it returns a placeholder string. If the value is a string, it converts
 * it from snake_case to Title Case. If the value is an object, it recursively
 * transforms each key-value pair into a string representation in the format
 * "(key: value)". For all other types, it uses a no-operation transform.
 *
 * @param value - The value to transform, which can be of any type.
 * @returns The transformed string representation of the value, or a placeholder
 *          if the input is nullish or an empty array.
 */
export function defaultValueTransform<T>(value?: T): string {
  if (isNullish(value)) {
    return PLACEHOLDER
  }
  if (Array.isArray(value) && value.length === 0) {
    return PLACEHOLDER
  }
  // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  if (typeof value! === 'string') {
    // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
    return convertSnakeCaseToSentenceCase(value!)
    // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  } else if (typeof value! === 'object') {
    // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
    return Object.entries(value!)
      .map(([key, val]) => `(${key}: ${defaultValueTransform(val)})`)
      .join(', ')
  }
  // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  return noopTransform(value!)
}

/**
 * Converts a given value to its string representation.
 *
 * If the input value is nullish (null or undefined),
 * it returns a placeholder string. Otherwise, it converts
 * the value to a string using the `String` constructor.
 *
 * @param value - The value to convert to a string, which can be of any type.
 * @returns The string representation of the value, or a placeholder if the input is nullish.
 */
function noopTransform<T>(value: T): string {
  if (isNullish(value)) {
    return PLACEHOLDER
  }
  // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
  return String(value!)
}

export function capitalize(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

/**
 * Formats large numbers into a shortened representation with appropriate suffixes (K, MM, B).
 *
 * Examples:
 * - 999 -> "999"
 * - 1,000 -> "1K" or "1.000K" (with consistentDecimals: 3)
 * - 1,000,000 -> "1MM" or "1.000MM" (with consistentDecimals: 3)
 * - 1,000,000,000 -> "1B" or "1.000B" (with consistentDecimals: 3)
 *
 * @param amount - The number to format as a string
 * @param options - Optional formatting options
 * @param options.consistentDecimals - If provided, always show this many decimal places with trailing zeros
 * @returns The formatted string with appropriate suffix
 */
export const formatLargeNumbers = (
  amount: string,
  options?: { consistentDecimals?: number },
) => {
  // Handle special characters that should be returned as-is
  if (amount === '—' || amount === '-') return amount

  // Remove commas from the input string for proper numeric conversion
  const stringValue = amount.replaceAll(',', '')
  const numberValue = Number(stringValue)

  // Return original input if it's not a valid number
  // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
  if (isNaN(numberValue)) return amount

  const consistentDecimals = options?.consistentDecimals

  /**
   * Helper function to format numbers
   * - With consistentDecimals: always show that many decimal places with trailing zeros
   * - Without: remove unnecessary trailing zeros
   * Example:
   *   formatNumber(1.0, 1)  -> "1" (default)
   *   formatNumber(1.0, 3, true) -> "1.000" (with consistent decimals)
   *   formatNumber(1.25, 3, true) -> "1.250" (with consistent decimals)
   */
  const formatNumber = (value: number, decimals: number) => {
    if (consistentDecimals !== undefined) {
      return value.toFixed(consistentDecimals)
    }
    return parseFloat(value.toFixed(decimals)).toString()
  }

  /**
   * 1,000,000,000+ (Billion)
   * - 1 decimal unless it's a whole number
   */
  if (numberValue >= 1_000_000_000) {
    return `${formatNumber(numberValue / 1_000_000_000, 1)}B`
  }

  /**
   * 1,000,000 - 999,999,999 (Million)
   * - 1 decimal unless it's a whole number
   */
  if (numberValue >= 1_000_000) {
    return `${formatNumber(numberValue / 1_000_000, 1)}MM`
  }

  /**
   * 100,000 - 999,999 (Hundred Thousand Range)
   * - Show appropriate decimals based on settings
   */
  if (numberValue >= 100_000) {
    return `${formatNumber(numberValue / 1_000, 0)}K`
  }

  /**
   * 10,000 - 99,999 (Tens of Thousands)
   * - Show appropriate decimals based on settings
   */
  if (numberValue >= 10_000) {
    const value = numberValue / 1_000
    return `${formatNumber(value, value % 1 === 0 ? 0 : 1)}K`
  }

  /**
   * 1,000 - 9,999 (Thousands)
   * - Show with appropriate decimals
   */
  if (numberValue >= 1_000) {
    return `${formatNumber(numberValue / 1_000, 1)}K`
  }

  /**
   * 1 - 999 (Small Numbers)
   * - No formatting, return as-is
   */
  return amount
}

export const formatMillions = (amount: string) => {
  return formatLargeNumbers(amount)
}

/**
 * Formats a number as compact money with currency prefix (e.g., $100k, $1.5M, $2B)
 * Handles both positive and negative values.
 *
 * @param value - The numeric value to format (number or string)
 * @param prefix - Currency prefix (default: "$")
 * @returns Formatted compact money string (e.g., "$100k", "$1.5M", "-$500k")
 *
 * @example
 * formatCompactMoney(1500) // "$1.5k"
 * formatCompactMoney(1000000) // "$1M"
 * formatCompactMoney(-500000) // "-$500k"
 * formatCompactMoney("2500000") // "$2.5M"
 */
export function formatCompactMoney(
  value: number | string,
  prefix = '$',
): string {
  const numValue = typeof value === 'string' ? parseFloat(value) : value
  // biome-ignore lint/suspicious/noGlobalIsNan: Need to check parseFloat result
  if (isNaN(numValue)) return String(value)

  const absValue = Math.abs(numValue)
  const sign = numValue < 0 ? '-' : ''
  const compact = (scaled: number, suffix: string) => {
    const rounded = Math.round(scaled * 10) / 10
    return `${sign}${prefix}${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)}${suffix}`
  }

  // Each threshold sits half a display step below the unit, so a value that
  // rounds up to 1000 of one unit is shown as 1 of the next.
  if (absValue >= 999_950_000) return compact(absValue / 1_000_000_000, 'B')
  if (absValue >= 999_950) return compact(absValue / 1_000_000, 'M')
  if (absValue >= 1_000) return compact(absValue / 1_000, 'k')
  // Sub-$1,000: round to the nearest cent so raw fractional values
  // (e.g. a budget-remaining of 211.544850) don't render every decimal.
  const rounded = Math.round(absValue * 100) / 100
  return `${sign}${prefix}${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(2)}`
}

/**
 * Formats asset class name to title case.
 *
 * @param assetClass - The asset class name to format.
 * @returns The formatted asset class name.
 */
export function formatAssetClass(assetClass: BondType): string {
  return assetClass === BondType.CD
    ? assetClass.toUpperCase()
    : convertToTitleCase(assetClass)
}
